import React, { useState, useEffect, useCallback } from 'react';
import AgentDetailView    from './AgentDetailView';
import AgentSelfView      from './AgentSelfView';
import TicketAnalysisPage from './TicketAnalysisPage';
import WallboardPage from './WallboardPage';
import WallboardZugang    from './WallboardZugang';
import Diagnose          from './Diagnose';
import UserMenu           from './UserMenu';
import Glossar            from './Glossar';
import Logo               from './Logo';

const API_BASE = process.env.REACT_APP_API_URL || '';

const ADMIN_TABS = [
  { id: 'mitarbeiter', label: 'Mitarbeiter Detail' },
  { id: 'analyse',     label: 'Ticket-Analyse'     },
  // Das Wallboard laeuft unabhaengig unter /wallboard auf dem Wandbildschirm;
  // hier ist es nur zusaetzlich erreichbar.
  { id: 'wallboard',   label: 'Wallboard'          },
];

// Agenten sehen ihre eigene Auswertung und das Wallboard — aber KEINE
// Ticket-Analyse (Fachentscheid 22.09.2026). Das Wallboard zeigt nur die
// Warteschlangen der eigenen Abteilungen, keine Personenkennzahlen.
const AGENT_TABS = [
  { id: 'meine',     label: 'Meine Auswertung' },
  { id: 'wallboard', label: 'Wallboard'        },
];

// Eine Reiterleiste fuer beide Ansichten — vorher stand das Markup nur in der
// Admin-Variante. Doppelt gepflegt waere es mit der Zeit auseinandergelaufen.
function Reiter({ tabs, aktiv, onWechsel }) {
  return tabs.map((t) => (
    <button key={t.id} onClick={() => onWechsel(t.id)} style={{
      padding: '12px 16px', fontSize: 13,
      fontWeight: aktiv === t.id ? 500 : 400,
      color:      aktiv === t.id ? 'var(--text-primary)' : 'var(--text-secondary)',
      background: 'transparent', border: 'none',
      borderBottom: aktiv === t.id ? '2px solid #185FA5' : '2px solid transparent',
      cursor: 'pointer', marginBottom: -1,
    }}>
      {t.label}
    </button>
  ));
}

function logout() {
  window.location.href = `${API_BASE}/auth/logout`;
}

// Liest das CSRF-Cookie (sorba_csrf, nicht httpOnly — vom Backend genau dafuer
// so gesetzt) aus und liefert die Header fuer zustandsaendernde Requests.
function csrfHeaders(extra = {}) {
  const match = document.cookie.match(/(?:^|; )sorba_csrf=([^;]+)/);
  return match ? { ...extra, 'X-CSRF-Token': match[1] } : extra;
}

export default function App() {
  const [auth,    setAuth]    = useState(null);   // null = lädt noch
  const [authErr, setAuthErr] = useState(null);
  const [tab,     setTab]     = useState('mitarbeiter');
  const [showGlossar, setShowGlossar] = useState(false);
  const [showWbZugang, setShowWbZugang] = useState(false);
  const [showDiagnose, setShowDiagnose] = useState(false);
  const [theme,   setTheme]   = useState(() => localStorage.getItem('theme') || 'light');

  const [trendRunning,    setTrendRunning]    = useState(false);
  const [reenrichRunning, setReenrichRunning] = useState(false);
  const [nachtragRunning, setNachtragRunning] = useState(false);

  // Echter Job-Status vom Server (laeuft/fertig/Fehler + Zeitstempel) — die
  // *Running-States oben zeigen nur "der Klick kam an", nicht "der Job ist
  // wirklich fertig" (Trigger-Endpoints sind fire-and-forget). Ohne das war
  // nicht sichtbar, ob z.B. "Archiv neu einlesen" nach 5 Min. schon fertig
  // war oder noch lief (bei vielen Monaten dauert das deutlich laenger).
  const [jobStatuses, setJobStatuses] = useState({});
  // Vollstaendigkeit des Jahresverlaufs (trend-weekly-Wochen-Blobs) — separat
  // vom Job-Status, weil "Job zuletzt fertig" nichts darueber aussagt, OB er
  // tatsaechlich alle Wochen geschrieben hat (Befund 2026-09-02: syncRecentUpdates
  // loeschte Wochen-Blobs schneller als syncTrendWeekly sie nachrechnete — der
  // Job selbst lief "erfolgreich", der Jahresverlauf war trotzdem wochenlang
  // loechrig, ohne dass irgendwo sichtbar war). Zeigt "fehlende" Wochen separat
  // von nur veralteter Schema-Version (die ist nach einem Bump normal/erwartet).
  const [trendCompleteness, setTrendCompleteness] = useState(null);
  // Kommt vom Server (SUPER_ADMIN_EMAILS), nicht mehr hartcodiert (Fachentscheid
  // 2026-08-25) — die 4 Worker-Trigger-Knoepfe sind eine engere Rollenstufe
  // als die normale Admin-Rolle, serverseitig via requireSuperAdmin erzwungen.
  const isSuperadmin = !!auth?.isSuperadmin;

  const fetchJobStatuses = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/diag/jobs`, { credentials: 'include' });
      if (res.ok) setJobStatuses(await res.json());
    } catch { /* ignore */ }
  }, []);

  const fetchTrendCompleteness = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/diag/trendweekly-schema`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      const missing = Array.isArray(data.weeks) ? data.weeks.filter(w => !w.vorhanden).length : null;
      setTrendCompleteness({ weeksTotal: data.weeksTotal, missing });
    } catch { /* ignore */ }
  }, []);

  // Alle 5s pollen solange ein Admin eingeloggt ist — 3 kleine Keys, kein
  // spuerbarer Overhead. Zusaetzlich sofort nach jedem Trigger-Klick.
  useEffect(() => {
    if (!isSuperadmin) return;
    fetchJobStatuses();
    fetchTrendCompleteness();
    const interval = setInterval(() => { fetchJobStatuses(); fetchTrendCompleteness(); }, 5000);
    return () => clearInterval(interval);
  }, [isSuperadmin, fetchJobStatuses, fetchTrendCompleteness]);

  const handleTriggerTrend = useCallback(async () => {
    setTrendRunning(true);
    try { await fetch(`${API_BASE}/api/admin/trigger-trend-weekly`, { method: 'POST', credentials: 'include', headers: csrfHeaders() }); }
    catch { /* ignore */ }
    finally { setTrendRunning(false); fetchJobStatuses(); fetchTrendCompleteness(); }
  }, [fetchJobStatuses, fetchTrendCompleteness]);

  const handleTriggerReenrich = useCallback(async () => {
    setReenrichRunning(true);
    try { await fetch(`${API_BASE}/api/admin/trigger-reenrich`, { method: 'POST', credentials: 'include', headers: csrfHeaders() }); }
    catch { /* ignore */ }
    finally { setReenrichRunning(false); fetchJobStatuses(); }
  }, [fetchJobStatuses]);

  // ── Status-Nachtrag: am 23.09.2026 entfernt, am 13.10.2026 zurueckgeholt ──
  //
  // Die Begruendung von damals war: „wurde nie benutzt, syncRecentUpdates
  // laeuft stuendlich von selbst". Das stimmte nicht — der stuendliche Lauf
  // KONNTE diese Faelle gar nicht sehen, weil seine zweite Suchabfrage ein
  // fest eingetragenes `-status:closed` trug (R2, behoben am 13.10.).
  //
  // Der Knopf wurde also genau in dem Moment entfernt, in dem man ihn
  // gebraucht haette. Gemessen am 13.10.: 359 Tickets in Juli bis September
  // tragen im Archiv einen veralteten Status, 350 davon sind inzwischen
  // geschlossen.
  //
  // Der stuendliche Lauf blickt 48 Stunden zurueck und holt das nicht nach.
  // Dafuer gibt es diesen Knopf.
  const handleTriggerNachtrag = useCallback(async () => {
    // ── In Haeppchen, nicht am Stueck ────────────────────────────────────
    //
    // Der erste Versuch ueber 120 Tage in EINEM Zug hat den Pod zerrissen:
    // `archiveStore` behaelt jeden geladenen Monat dauerhaft im Speicher, und
    // fuenf Monate auf einmal sind bei 768 MB zu viel (gemessen 87 % RSS eine
    // Minute vor dem Absturz).
    //
    // Jetzt: die Marke wird einmal weit zurueckgesetzt — der Endpunkt schiebt
    // sie NIE vorwaerts —, und jeder Druck arbeitet 30 Tage ab. Viermal
    // druecken deckt die 120 Tage, jedes Mal mit einem Viertel des
    // Speicherbedarfs.
    if (!window.confirm(
      'Status-Nachtrag starten?\n\n'
      + 'Arbeitet 30 Tage ab und merkt sich, wie weit er gekommen ist. '
      + 'Für die vollen 120 Tage also viermal drücken — jeweils erst, wenn '
      + 'der vorige Lauf fertig ist.\n\n'
      + 'Rund 60 Zendesk-Suchen je Lauf, ein bis zwei Minuten. '
      + 'Läuft im Hintergrund.')) return;
    setNachtragRunning(true);
    try {
      const r = await fetch(`${API_BASE}/api/admin/trigger-recent-updates`, {
        method: 'POST', credentials: 'include',
        headers: { ...csrfHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ days: 120, maxFenster: 30 }),
      });
      // Seit 16.10.2026 antwortet der Endpunkt unter ARCHIV_QUELLE=strom mit
      // 409 und einer Begruendung. Ohne diese Zeile sah der Knopf aus, als
      // haette er gearbeitet.
      if (!r.ok) {
        const d = await r.json().catch(() => null);
        window.alert(d?.error || `Status-Nachtrag nicht gestartet (HTTP ${r.status}).`);
      }
    } catch { /* ignore */ }
    finally { setNachtragRunning(false); fetchJobStatuses(); }
  }, [fetchJobStatuses]);

  // Ebenfalls am 23.09.2026 entfernt: "Archive voll neu laden". Der war gegen
  // die stille 500er-Fetch-Kappe gebaut — deren Ursache ist seit 15.07.
  // behoben und der Nachhol-Lauf am 28.08. durch. Der Endpunkt existiert
  // weiterhin, er ist nur nicht einen Fehlklick weit entfernt (der Rebuild
  // loeste 15-30 Min Volllast aus).

  // Formatiert den Server-Job-Status als kurze Subtitle-Zeile unter dem Button.
  function formatJobStatus(s) {
    if (!s || s.status === 'idle') return null;
    const fmt = iso => iso ? new Date(iso).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' }) : '?';
    if (s.status === 'running') return `läuft seit ${fmt(s.startedAt)} Uhr`;
    if (s.status === 'error')   return `Fehler (${fmt(s.finishedAt)} Uhr): ${s.error}`;
    if (s.status === 'done')    return `zuletzt fertig: ${fmt(s.finishedAt)} Uhr`;
    return null;
  }

  // Theme am <html> setzen + merken (data-theme="dark" steuert theme.css)
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('theme', theme); } catch { /* ignore */ }
  }, [theme]);
  const toggleTheme = () => setTheme(t => (t === 'dark' ? 'light' : 'dark'));

  // ── Benutzer beim Start laden ──────────────────────────────────────────────
  useEffect(() => {
    fetch(`${API_BASE}/api/me`, { credentials: 'include' })
      .then(res => {
        if (res.status === 401) {
          // Nicht eingeloggt — zeigt den Login-Link (/auth/login), der Browser
          // wird von dort zu Microsoft weitergeleitet (eigener OIDC-Flow, kein
          // Easy Auth mehr — siehe backend/src/routes/auth.js).
          setAuthErr('not_authenticated');
          return null;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(user => {
        if (user) setAuth(user);
      })
      .catch(() => setAuthErr('network_error'));
  }, []);

  // ── Keep-Alive ──────────────────────────────────────────────────────────────
  // Seit dem eigenen OIDC-Login (kein Easy Auth mehr) laeuft die Session-
  // Cookie nach fester Dauer ab (8h, siehe requireAuth.js) — dieser Ping
  // verlaengert sie NICHT mehr automatisch, er sorgt nur dafuer, dass ein
  // abgelaufenes Login zeitnah bemerkt wird statt erst beim naechsten
  // Daten-Fetch mitten in der Arbeit.
  //
  // Zwei Mechanismen, kein Visibility-Gate auf dem Intervall:
  //   1. Intervall alle 5 Min — laeuft auch im Hintergrund-Tab weiter. Browser
  //      koennen setInterval in inaktiven Tabs drosseln, aber die paar Calls
  //      kosten nichts (siehe Analyse), daher kein Grund das zu pausieren.
  //   2. Sofort-Ping bei visibilitychange → 'visible' — faengt genau den Fall
  //      "Tab war lange im Hintergrund/Bildschirm gesperrt, Nutzer kommt
  //      zurueck" ab, BEVOR irgendein Daten-Fetch laeuft. Browser-Timer-
  //      Throttling betrifft dieses Event nicht (echtes Browser-Event,
  //      kein Timer).
  useEffect(() => {
    if (!auth) return; // erst nach erfolgreichem Login starten
    const KEEPALIVE_INTERVAL_MS = 5 * 60 * 1000; // 5 Min

    const ping = () => {
      fetch(`${API_BASE}/api/me`, { credentials: 'include' }).catch(() => { /* still — naechster Versuch in 5 Min */ });
    };
    const pingOnReturn = () => {
      if (document.visibilityState === 'visible') ping();
    };

    const id = setInterval(ping, KEEPALIVE_INTERVAL_MS);
    document.addEventListener('visibilitychange', pingOnReturn);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', pingOnReturn);
    };
  }, [auth]);

  // ── Ladescreen ────────────────────────────────────────────────────────────
  if (!auth && !authErr) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: '100vh', fontFamily: 'system-ui, sans-serif', background: '#F8F8F7',
      }}>
        <div style={{ fontSize: 13, color: '#888' }}>Anmeldung wird geprüft…</div>
      </div>
    );
  }

  // ── Fehlerscreen (nur Entwicklung / Konfigurationsproblem) ────────────────
  if (authErr) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: '100vh',
        fontFamily: 'system-ui, sans-serif', background: '#F8F8F7',
      }}>
        <div style={{
          background: '#fff', borderRadius: 12, padding: '40px 48px',
          boxShadow: '0 2px 16px rgba(0,0,0,0.08)', minWidth: 320, textAlign: 'center',
        }}>
          <div style={{ fontSize: 22, fontWeight: 600, marginBottom: 8 }}>Support-Dashboard</div>
          {authErr === 'not_authenticated' ? (
            <>
              <div style={{ fontSize: 13, color: '#666', marginBottom: 24 }}>
                Nicht angemeldet. Bitte melde dich mit deinem Microsoft-Konto an.
              </div>
              <a href={`${API_BASE}/auth/login`} style={{
                display: 'inline-block', padding: '12px 28px', borderRadius: 8,
                background: '#185FA5', color: '#fff', fontSize: 14,
                fontWeight: 500, textDecoration: 'none',
              }}>
                Mit Microsoft anmelden
              </a>
            </>
          ) : (
            <div style={{ fontSize: 13, color: '#c0392b' }}>
              Verbindung zum Server fehlgeschlagen.<br />Bitte Seite neu laden.
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Agent-Ansicht ─────────────────────────────────────────────────────────
  if (auth.role === 'agent') {
    return (
      <div style={{ fontFamily: 'system-ui, sans-serif', minHeight: '100vh' }}>
        <div style={{
          borderBottom: '1px solid var(--border)', padding: '0 1rem',
          display: 'flex', alignItems: 'center', gap: 0,
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 10, marginRight: 24, padding: '10px 0' }}>
            <Logo height={53} />
            <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1 }}>Support-Dashboard</span>
          </span>
          <Reiter tabs={AGENT_TABS} aktiv={tab === 'wallboard' ? 'wallboard' : 'meine'} onWechsel={setTab} />
          <div style={{ marginLeft: 'auto' }}>
            <UserMenu name={auth.name} photoUrl={`${API_BASE}/api/me/photo`} theme={theme} onToggleTheme={toggleTheme} onOpenGlossar={() => setShowGlossar(true)} onLogout={logout} />
          </div>
        </div>
        {/* Alles ausser 'wallboard' zeigt die eigene Auswertung — der
            Anfangswert von `tab` stammt aus der Admin-Variante und ist hier
            kein gueltiger Reiter. */}
        {tab === 'wallboard'
          ? <WallboardPage />
          : <AgentSelfView agentId={auth.zdAgentId} agentName={auth.name} />}
        {showGlossar && <Glossar variant="agent" onClose={() => setShowGlossar(false)} />}
      </div>
    );
  }

  // ── Admin-Ansicht ─────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: 'system-ui, sans-serif', minHeight: '100vh' }}>
      <div style={{
        borderBottom: '1px solid var(--border)', padding: '0 1rem',
        display: 'flex', alignItems: 'center', gap: 0,
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10, marginRight: 24, padding: '10px 0' }}>
          <Logo height={53} />
          <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1 }}>Support-Dashboard</span>
        </span>
        <Reiter tabs={ADMIN_TABS} aktiv={tab} onWechsel={setTab} />
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <UserMenu
            name={auth.name}
            photoUrl={`${API_BASE}/api/me/photo`}
            theme={theme}
            onToggleTheme={toggleTheme}
            onOpenGlossar={() => setShowGlossar(true)}
            onLogout={logout}
            adminActions={isSuperadmin ? [
              { key: 'trend',    icon: '⚙',  label: 'Jahresverlauf neu berechnen', title: 'Trend-Worker sofort ausführen',
                running: trendRunning || jobStatuses.trendWeekly?.status === 'running', onClick: handleTriggerTrend,
                status: [
                  formatJobStatus(jobStatuses.trendWeekly),
                  trendCompleteness && (trendCompleteness.missing > 0
                    ? `⚠ ${trendCompleteness.missing}/${trendCompleteness.weeksTotal} Wochen fehlen`
                    : `${trendCompleteness.weeksTotal}/${trendCompleteness.weeksTotal} Wochen vorhanden`),
                ].filter(Boolean).join(' · ') || null },
              { key: 'reenrich', icon: '🔄', label: 'Archiv neu einlesen',         title: 'bearbeitungMin aller Monate neu berechnen',
                running: reenrichRunning || jobStatuses.reenrichArchives?.status === 'running', onClick: handleTriggerReenrich,
                status: formatJobStatus(jobStatuses.reenrichArchives) },
              { key: 'nachtrag', icon: '📌', label: 'Status-Nachtrag (30 Tage je Lauf)',
                title: 'Holt für jeden Tag die geänderten Tickets erneut und führt die Monats-Archive nach. Merkt sich, wie weit er gekommen ist — mehrmals drücken, bis er nichts mehr findet.',
                running: nachtragRunning || jobStatuses.recentUpdates?.status === 'running', onClick: handleTriggerNachtrag,
                status: formatJobStatus(jobStatuses.recentUpdates) },
              { key: 'diagnose', icon: '🩺', label: 'Diagnose',
                title: 'Zustand der Hintergrund-Auftraege — reine Anzeige',
                onClick: () => setShowDiagnose(true) },
              { key: 'wallboard', icon: '🖥', label: 'Wallboard-Zugang',
                title: 'Benutzername und Passwort für die Raumanzeige unter /wallboard setzen',
                onClick: () => setShowWbZugang(true) },
            ] : undefined}
          />
        </div>
      </div>
      {tab === 'mitarbeiter' && <AgentDetailView auth={auth} />}
      {tab === 'analyse'     && <TicketAnalysisPage />}
      {tab === 'wallboard'   && <WallboardPage />}
      {showGlossar && <Glossar variant="admin" onClose={() => setShowGlossar(false)} />}
      {showWbZugang && <WallboardZugang onClose={() => setShowWbZugang(false)} />}
      {showDiagnose && <Diagnose onClose={() => setShowDiagnose(false)} />}
    </div>
  );
}
